// supervisor-orphan-reclaim-forensics.ts
//
// O-2 fix (dogfood recheck 260925 follow-up): `reconcileOrphanedTicketsAtStartup`'s
// `git reset --hard HEAD` + `git clean -fd` (NEW-2 fix, supervisor-mission-
// run-startup.ts) discards a killed attempt's uncommitted diff with NO trace
// at all. Dogfood recheck 260925 measured journal.jsonl carrying only
// `attempt_recovered` ("ticket back to todo") — never "N files worth of work
// were discarded, here is the patch". A reviewer auditing a reclaim after the
// fact had nothing to look at.
//
// This module archives the killed attempt's diff (reusing attempt-forensics.ts
// — same `<control_root>/attempts/<attemptId>/forensics/` shape contract_error
// already uses) and journals a `worktree_reset_on_reclaim` event, BOTH before
// the caller's `recoverOwnedCheckout` destroys the evidence. Split into its
// own file (not folded into supervisor-mission-run-startup.ts, already near
// its 200-line budget) per the same one-concern-per-file convention every
// other supervisor-*.ts split in this codebase follows.
import { randomUUID } from "node:crypto";
import { promises as fs } from "node:fs";
import { archiveAttemptForensics } from "./attempt-forensics.js";
import type { MissionStore } from "./mission-store.js";
import type { AttemptStateFile } from "./supervisor-attempt-state.js";
import { retryStateFor } from "./supervisor-attempt-state.js";

export interface ArchiveForensicsBeforeReclaimResetInput {
  store: MissionStore;
  controlRoot: string;
  missionWorkspace: string;
  ticketIds: readonly string[];
  attemptState: AttemptStateFile;
}

/**
 * Archives forensics + journals `worktree_reset_on_reclaim` for every
 * reclaimable ticket about to have its worktree reset. `attemptState` must be
 * read BEFORE `applyReclaimableRecoveries` bumps `physical_runs` (the caller,
 * supervisor-mission-run-startup.ts, reads it once and reuses it here). The
 * killed attempt's number is `physical_runs + 1`: the counter is bumped only
 * when an attempt finalizes, so while one is in flight the recorded count is
 * the attempt BEFORE it (06g đợt 2 — measured in the SIGKILL e2e, where the
 * copy used to land in `T-xxxx-0`). Same `<ticketId>-<attemptNo>` convention
 * supervisor-forensics-archival.ts uses for every other forensics call site.
 */
export async function archiveForensicsBeforeReclaimReset(
  input: ArchiveForensicsBeforeReclaimResetInput,
): Promise<void> {
  for (const ticketId of input.ticketIds) {
    const attemptNo = retryStateFor(input.attemptState, ticketId).physical_runs + 1;
    const attemptId = `${ticketId}-${attemptNo}`;
    const { manifestPath } = await archiveAttemptForensics({
      controlRoot: input.controlRoot,
      repoDir: input.missionWorkspace,
      attemptId,
      reason: "worktree_reset_on_reclaim: orphan recovery discards this attempt's uncommitted diff",
      // 06g R2e: the reset below deletes untracked files — keep their (redacted) contents too.
      captureUntrackedContents: true,
    });
    const manifest = JSON.parse(await fs.readFile(manifestPath, "utf8")) as {
      untrackedFiles: string[];
    };
    const patchPath = manifestPath.replace(/manifest\.json$/, "worktree-changes.patch");
    const patch = await fs.readFile(patchPath, "utf8");
    // Tracked-change count from the patch's diff headers + untracked file
    // count from the manifest — good enough for an audit trail (exact count
    // is not load-bearing; the patch itself is the source of truth).
    const trackedFileCount = (patch.match(/^diff --git /gm) ?? []).length;
    const fileCount = trackedFileCount + manifest.untrackedFiles.length;

    await input.store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "worktree_reset_on_reclaim",
          ticket: ticketId,
          attemptId,
          fileCount,
          patchPath,
        },
      },
    ]);
  }
}
